import type { SalonStockerHttpTypes } from "@repo/api-sdk";

/**
 * What a seller's offer may show as its price while the products-pricing call
 * is in flight (KAN-343).
 *
 * - `ready`   - show the price on the offer (today's behaviour).
 * - `loading` - show a loader. The offer's price is the search index's retail
 *               price, which a claimed salon must never see.
 * - `error`   - pricing failed; show an error state, never the index price.
 */
export type OfferPriceState = "ready" | "loading" | "error";

/** The slice of a react-query `status` this module cares about. */
export type PricingStatus = "pending" | "error" | "success";

/**
 * Which sellers the salon has claimed a trade account with.
 *
 * - `guest`   - nobody is logged in, so there is no trade price to protect.
 * - `unknown` - a salon is (or may be) logged in but its claims have not been
 *               read yet (still loading, or the read failed). Treated like a
 *               claimed seller until pricing settles, so no retail price
 *               flashes in the gap.
 * - `known`   - the claimed seller ids.
 */
export type ClaimedSellers =
  | { kind: "guest" }
  | { kind: "unknown" }
  | { kind: "known"; ids: ReadonlySet<string> };

export const GUEST_CLAIMS: ClaimedSellers = { kind: "guest" };
export const UNKNOWN_CLAIMS: ClaimedSellers = { kind: "unknown" };

/** The seller ids whose Supplier Match status is `claimed`. */
export function claimedSellerIds(
  matches: ReadonlyArray<
    Pick<
      SalonStockerHttpTypes.StoreSupplierMatchStatusView,
      "seller_id" | "status"
    >
  >
): Set<string> {
  return new Set(
    matches.filter((m) => m.status === "claimed").map((m) => m.seller_id)
  );
}

/**
 * Whether an offer from `sellerId` may show the price it carries yet.
 *
 * The price in a search hit comes from the index and is the retail price. For a
 * claimed seller the products-pricing response is the only source of the right
 * (trade) price, so until it has returned the offer has no price to show, and
 * if it fails there is none. Once pricing has succeeded every offer it priced
 * carries the API's price (trade for claimed sellers, the normal price
 * otherwise), so the claim set no longer matters for those.
 */
export function resolveOfferPriceState(
  sellerId: string,
  claims: ClaimedSellers,
  pricingStatus: PricingStatus,
  /**
   * Whether the pricing response had a row for this very offer. An offer it
   * leaves out still carries the index's retail price, so for a seller that may
   * be claimed that is no price at all: fail closed.
   */
  hasPricingRow = true
): OfferPriceState {
  if (claims.kind === "guest") return "ready";
  if (claims.kind === "known" && !claims.ids.has(sellerId)) return "ready";
  if (pricingStatus === "success") return hasPricingRow ? "ready" : "error";
  return pricingStatus === "error" ? "error" : "loading";
}

/** What a card asks, per offer, before showing the offer's price. */
export type OfferPriceResolver = (offer: {
  id: string;
  seller_id: string;
}) => OfferPriceState;

/**
 * The single state for a block that summarises several offers (a card's price
 * range): any offer that cannot show its price holds the whole summary back,
 * because the range's min and max are computed across all of them.
 */
export function combineOfferPriceStates(
  states: Iterable<OfferPriceState>
): OfferPriceState {
  let combined: OfferPriceState = "ready";
  for (const state of states) {
    if (state === "error") return "error";
    if (state === "loading") combined = "loading";
  }
  return combined;
}
